import React, { useRef } from "react";
import { InformationIcon } from "vega-icons";

import { ScrollIndicatorButton } from "@vega-core/components/Table/CoreTable/components/ScrollIndicatorButton";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const DEFAULT_SCROLLABLE_COLUMN_WIDTH = "10rem";

export type Stage = {
  title: string;
  value: string;
  description?: string;
  secondaryValues?: string[];
  color: string;
  activeColor?: string;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
};

export type StagesProps = {
  header?: React.ReactNode;
  minColumnWidth?: string;
  scrollable?: boolean;
  stages: Stage[];
  variant?: "compact" | "default";
  emptyState?: Stage[];
  isLoading?: boolean;
};

export const Stages = ({
  header,
  minColumnWidth = DEFAULT_SCROLLABLE_COLUMN_WIDTH,
  scrollable,
  stages = [],
  variant = "default",
  emptyState,
}: StagesProps) => {
  const displayStages = !stages.length && emptyState ? emptyState : stages;
  const shouldShowEmptyState = !stages.length && !!emptyState;
  const scrollRef = useRef<HTMLDivElement>(null);

  const stagesContent = (
    <div
      className={cnTwMerge(
        "flex",
        !scrollable && "border border-text/secondary/20",
        shouldShowEmptyState && "opacity-50",
      )}
    >
      {displayStages.map((stage, index) => {
        const isInteractive = !!stage.onClick && !stage.disabled;

        return (
          <div
            className={cnTwMerge(
              "flex-1 flex flex-col text-left min-w-0",
              index < displayStages.length - 1 &&
                "border-r border-text/secondary/20",
              scrollable && "border-y border-text/secondary/20",
              scrollable && index === 0 && "border-l",
              scrollable &&
                index === displayStages.length - 1 &&
                "border-r border-text/secondary/20",
            )}
            key={stage.title}
            style={scrollable ? { minWidth: minColumnWidth } : undefined}
          >
            <div
              aria-hidden
              className={cnTwMerge(stage.color, "h-1 shrink-0")}
            />
            <div
              aria-disabled={stage.disabled || undefined}
              aria-pressed={isInteractive ? !!stage.active : undefined}
              className={cnTwMerge(
                "p-4 flex-1 min-w-0",
                variant === "compact" && "p-3",
                stage.disabled && "opacity-40",
                isInteractive && "cursor-pointer hover:bg-text/secondary/10",
                isInteractive && stage.active && stage.activeColor,
              )}
              onClick={isInteractive ? stage.onClick : undefined}
              onKeyDown={
                isInteractive
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        stage.onClick!();
                      }
                    }
                  : undefined
              }
              role={isInteractive ? "button" : undefined}
              tabIndex={isInteractive ? 0 : undefined}
            >
              <div
                className={cnTwMerge(
                  "grid gap-2 content-start",
                  stage.secondaryValues !== undefined
                    ? "grid-rows-4"
                    : "grid-rows-3",
                )}
              >
                <div
                  className={cnTwMerge(
                    "flex items-center gap-1",
                    scrollable && "min-w-0",
                  )}
                >
                  <h3
                    className={cnTwMerge(
                      "body-small-semibold whitespace-nowrap",
                      stage.disabled
                        ? "text-text-disabled"
                        : "text-text-primary",
                      variant === "compact" && "body-base-semibold",
                    )}
                  >
                    {stage.title}
                  </h3>
                  {stage.tooltip ? (
                    <Tooltip
                      className="gap-2 p-2"
                      content={stage.tooltip}
                      title={
                        <span className="body-small-semibold text-text/primary">
                          {stage.title}
                        </span>
                      }
                    >
                      <InformationIcon
                        className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-text/secondary cursor-pointer hover:text-text/primary transition-colors"
                        onClick={(event) => event.stopPropagation()}
                      />
                    </Tooltip>
                  ) : null}
                </div>

                <div
                  className={cnTwMerge(
                    "body-semi-large-semibold",
                    stage.disabled ? "text-text-disabled" : "text-text-primary",
                    variant === "compact" && "header-4",
                  )}
                >
                  {stage.value}
                </div>

                {stage.secondaryValues !== undefined ? (
                  <div
                    className={cnTwMerge(
                      "body-small-medium text-text/secondary flex flex-wrap items-center gap-x-2 gap-y-1 min-h-5",
                      variant === "compact" && "body-small-regular",
                    )}
                  >
                    {stage.secondaryValues.map((amount) => (
                      <span key={amount}>{amount}</span>
                    ))}
                  </div>
                ) : null}

                <div
                  className={cnTwMerge(
                    "flex items-center",
                    "text-text-secondary",
                    variant === "compact" && "body-small-regular",
                  )}
                >
                  {stage.description ? (
                    <span className="body-small-medium">
                      {stage.description}
                    </span>
                  ) : (
                    <span className="body-small-medium">&nbsp;</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="stages-container">
      {header ? (
        <div
          className={cnTwMerge(
            "flex items-center justify-between mb-6",
            variant === "compact" && "mb-4",
          )}
        >
          {header}
        </div>
      ) : null}

      {scrollable ? (
        <div className="relative w-full min-w-0">
          <div
            className="max-w-[100vw] w-full min-w-0 overflow-x-auto"
            ref={scrollRef}
          >
            {stagesContent}
          </div>
          <ScrollIndicatorButton scrollRef={scrollRef} />
        </div>
      ) : (
        stagesContent
      )}
    </div>
  );
};
